import { getUrlParamFromCookie } from './cookies';
import getUrlSearchParams from './getUrlSearchParams';
import { getAnonymousId } from './getAnonymousId';

import { UrlByEnv, envEnum } from '../context/PageData';
import { UserData } from '../lib/referrals/getUserDataById';
import { initialize } from 'src/lib/onboarding/initialize';
import { snakeToCamel } from './camelSnakeConverter';
import { usePublicRuntimeConfig } from '../hooks/usePublicRuntimeConfig';

interface WebRegBits {
  baseUrl: string;
  phoneNumber?: string;
  forwardedUrlParams?: string[] | null;
  bypassPhoneNumberEntry: boolean;
  forwardedCtaParams?: string | null;
  forwardedButtonParams?: string | null;
  emailAddress?: string;
  advocateUserData?: UserData | null;
  role?: string;
  intent?: string;
}

export function getRedirectByEnv(ctaDestinationUrls: UrlByEnv = {}) {
  const { nodeEnv } = usePublicRuntimeConfig();

  const env = nodeEnv as keyof typeof envEnum;
  // Use webreg url from contentful or default to env variable
  const byEnv = ctaDestinationUrls[envEnum[env]];
  return byEnv || ctaDestinationUrls.production;
}

function isProtocolUrl(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(url);
}

function isNonHttpProtocolUrl(url: string): boolean {
  if (!isProtocolUrl(url)) {
    return false;
  }
  const lowerUrl = url.toLowerCase();
  return !lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://');
}

export function appendParams({
  baseUrl,
  phoneNumber,
  forwardedUrlParams,
  bypassPhoneNumberEntry,
  forwardedCtaParams,
  forwardedButtonParams,
  emailAddress,
  advocateUserData,
  role,
  intent
}: WebRegBits) {
  if (!baseUrl) return '';

  if (baseUrl.startsWith('#')) return baseUrl;

  if (isNonHttpProtocolUrl(baseUrl)) {
    return baseUrl;
  }

  let url: URL;
  try {
    if (baseUrl.startsWith('/')) {
      url = new URL(
        baseUrl,
        typeof window !== 'undefined'
          ? window.location.origin
          : 'https://greenlight.com'
      );
    } else {
      url = new URL(baseUrl);
    }
  } catch (error) {
    return baseUrl;
  }

  const buttonParams = asLeadingAmp(forwardedButtonParams);
  const ctaParams = asLeadingAmp(forwardedCtaParams);
  const withForwardedParams = ensureForwardedParams(forwardedUrlParams);
  const addtlParams = getUrlSearchParams(
    withForwardedParams,
    buttonParams,
    ctaParams
  );

  // Keep detection available for future behavior toggles
  const _isOnboardingTarget = computeIsOnboardingTarget(url);
  if (_isOnboardingTarget) {
    // no-op; retained intentionally for future gating needs
  }

  const anonymousId = getAnonymousId();
  const utmSource = getUrlParamFromCookie('utm_source');

  appendPhoneAndEmail(url, {
    bypassPhoneNumberEntry,
    phoneNumber,
    emailAddress
  });

  if (anonymousId) {
    url.searchParams.append('anonymous_id', encodeURIComponent(anonymousId));
  }

  if (!utmSource) {
    url.searchParams.append('utm_source', 'incognito');
  }

  if (role) {
    url.searchParams.append('role', role);
  }

  if (intent) {
    url.searchParams.append('intent', intent);
  }

  // Apply parameters from cookies and forwarded params
  const cookieParams = [
    utmSource,
    getUrlParamFromCookie('utm_medium'),
    getUrlParamFromCookie('utm_campaign'),
    getUrlParamFromCookie('utm_term'),
    getUrlParamFromCookie('utm_content'),
    getUrlParamFromCookie('irclickid'),
    getUrlParamFromCookie('_branch_match_id'),
    getUrlParamFromCookie('max_redemption_limit_reached'),
    getUrlParamFromCookie('referral_reward_value'),
    advocateUserData?.isTeen
      ? `&advocate-is-teen=${advocateUserData.isTeen}`
      : ''
  ];

  for (const q of [...cookieParams, addtlParams, ctaParams, buttonParams]) {
    appendQueryString(url, q);
  }

  return url.toString();
}

function asLeadingAmp(value?: string | null): string {
  return value ? `&${value}` : '';
}

function ensureForwardedParams(params?: string[] | null): string[] {
  const next = Array.isArray(params) ? [...params] : [];
  if (!next.includes('gclid')) next.push('gclid');
  return next;
}

function computeIsOnboardingTarget(target: URL): boolean {
  try {
    const host = target.hostname;
    const isProdRegister = host === 'register.greenlight.com';
    const isStageDevOnboarding =
      /^onboarding\.[^.]+\.greenlight\.gl-tech\.io$/.test(host);
    const inOnboardingPath = target.pathname.startsWith('/onboarding');
    return (isProdRegister || isStageDevOnboarding) && inOnboardingPath;
  } catch (_e) {
    return false;
  }
}

function appendPhoneAndEmail(
  url: URL,
  {
    bypassPhoneNumberEntry,
    phoneNumber,
    emailAddress
  }: {
    bypassPhoneNumberEntry: boolean;
    phoneNumber?: string;
    emailAddress?: string;
  }
) {
  if (bypassPhoneNumberEntry === false && phoneNumber) {
    url.searchParams.append('phoneNumber', phoneNumber);
  }
  if (bypassPhoneNumberEntry === false && emailAddress) {
    url.searchParams.append('emailAddress', emailAddress);
  }
}

function appendQueryString(url: URL, query?: string) {
  if (!query) return;
  const trimmed = query.startsWith('&') ? query.substring(1) : query;
  if (!trimmed) return;
  const pairs = trimmed.split('&').filter(Boolean);
  for (const pair of pairs) {
    const [k, v = ''] = pair.split('=');
    if (k) url.searchParams.append(k, v);
  }
}

export async function webregInitialize(
  webregRedirectUrl: string
): Promise<boolean> {
  const url = new URL(webregRedirectUrl);
  const params = new URLSearchParams(url.search);
  const configuredParams = configureParams(params);
  return await initialize(configuredParams);
}

function configureParams(params: URLSearchParams) {
  const paramsObj = Object.fromEntries(params.entries());
  return snakeToCamel(paramsObj);
}
